import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {borderRadius, colors, fontWeight, shadows} from '@/styles/tokens';

export interface WhiteLabelComparisonRow {
    factor: string;
    whiteLabel: string;
    inHouse: string;
    openSource: string;
}

interface WhiteLabelComparisonProps {
    heading: string;
    subheading: string;
    data: WhiteLabelComparisonRow[];
}

const tableContainerSx = {
    border: '1px solid #E5E7EB',
    borderRadius: borderRadius.xl,
    overflowX: 'auto',
    boxShadow: shadows.awardCardElevated,
    '&::-webkit-scrollbar': {height: '6px'},
    '&::-webkit-scrollbar-thumb': {backgroundColor: colors.neutral.borderLight, borderRadius: borderRadius['10']}
} as const;

const headerCellBaseSx = {
    fontWeight: fontWeight.semibold,
    fontSize: '16px',
    color: '#fff',
    textAlign: 'center' as const,
    py: 2.5,
    borderBottom: 'none',
    borderRight: '1px solid rgba(255,255,255,0.2)',
    '&:last-child': {borderRight: 'none'}
} as const;

const factorCellSx = {
    fontWeight: fontWeight.semibold,
    fontSize: '14px',
    color: colors.primary.deep,
    borderRight: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB',
    py: 2.5,
    px: 2,
    minWidth: 180
} as const;

const whiteLabelCellSx = {
    fontWeight: fontWeight.bold,
    fontSize: '14px',
    color: '#027a48',
    textAlign: 'center' as const,
    borderRight: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB',
    py: 2.5,
    px: 2,
    bgcolor: 'rgba(2, 122, 72, 0.06)'
} as const;

const otherCellSx = {
    fontWeight: fontWeight.regular,
    fontSize: '14px',
    color: colors.text.primary,
    textAlign: 'center' as const,
    borderRight: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB',
    py: 2.5,
    px: 2,
    '&:last-child': {borderRight: 'none'}
} as const;

const WhiteLabelComparison = ({heading, subheading, data}: WhiteLabelComparisonProps) => {
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h2" component="h2" mb={3} fontSize={{xs: 32, sm: 52}} fontWeight={800} className="font-headers">
                        {heading}
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text">
                        {subheading}
                    </Typography>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
                    <Table sx={{minWidth: 700}} aria-label="white-label vs alternatives comparison table">
                        <TableHead>
                            <TableRow
                                sx={{
                                    background: 'linear-gradient(180deg, #1f3f7a 0%, #0f1c36 100%)',
                                    '& .MuiTableCell-root': {background: 'transparent'}
                                }}
                            >
                                <TableCell className="font-headers" sx={{...headerCellBaseSx, textAlign: 'left', minWidth: 180}} />
                                <TableCell
                                    className="font-headers"
                                    sx={{...headerCellBaseSx, minWidth: 220, background: 'rgba(255,255,255,0.15) !important'}}
                                >
                                    White Label (Debut)
                                </TableCell>
                                <TableCell className="font-headers" sx={{...headerCellBaseSx, minWidth: 220}}>
                                    Build In-House
                                </TableCell>
                                <TableCell className="font-headers" sx={{...headerCellBaseSx, minWidth: 180}}>
                                    Open Source
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data?.map((row, idx) => {
                                const isLastRow = idx === data.length - 1;
                                const isEven = idx % 2 === 0;
                                const rowBg = isEven ? '#f7f9fc' : '#ffffff';
                                const noBorder = isLastRow ? 'none' : undefined;

                                return (
                                    <TableRow key={idx} sx={{bgcolor: rowBg}}>
                                        <TableCell
                                            className="font-headers"
                                            sx={{...factorCellSx, borderBottom: noBorder ?? '1px solid #E5E7EB'}}
                                        >
                                            {row.factor}
                                        </TableCell>
                                        <TableCell
                                            className="font-regular-text"
                                            sx={{...whiteLabelCellSx, borderBottom: noBorder ?? '1px solid #E5E7EB'}}
                                        >
                                            {row.whiteLabel}
                                        </TableCell>
                                        <TableCell
                                            className="font-regular-text"
                                            sx={{...otherCellSx, borderBottom: noBorder ?? '1px solid #E5E7EB'}}
                                        >
                                            {row.inHouse}
                                        </TableCell>
                                        <TableCell
                                            className="font-regular-text"
                                            sx={{
                                                ...otherCellSx,
                                                borderRight: 'none',
                                                borderBottom: noBorder ?? '1px solid #E5E7EB'
                                            }}
                                        >
                                            {row.openSource}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MUIContainer>
        </Box>
    );
};

export default memo(WhiteLabelComparison);
